import type { ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button } from '@autop/ui/src/button';

import { useUnsubscribe } from '@/entities/consent';
import { useSession } from '@/entities/session';
import { COMPANY } from '@/shared/config';

/** 동의 시각과 같은 형식. 서버가 붙기 전까지 화면이 쓰는 표기용 값이다. */
const TODAY = new Date().toISOString().slice(0, 10).replace(/-/g, '.');

function Shell({ title, children }: { title: string; children: ReactElement }): ReactElement {
  return (
    <div className="u-container flex justify-center py-16">
      <div className="flex w-full max-w-[28rem] flex-col gap-5">
        <h1 className="text-[1.5rem] font-bold tracking-[-0.04em]">{title}</h1>
        {children}
      </div>
    </div>
  );
}

/**
 * 광고성 정보 수신거부 — 메일 하단 링크가 닿는 곳.
 *
 * **로그인을 요구하지 않는다.** 정보통신망법 제50조 제4항은 수신자가 수신거부 의사를
 * 쉽게 밝힐 수 있게 하라고 정한다. 계정을 찾아 로그인부터 하게 만들면 그 조건을 못 지킨다.
 * 본인 확인은 메일에 실어 보낸 토큰이 대신한다.
 *
 * **링크를 열자마자 처리하지 않고 버튼을 한 번 더 누르게 한다.** 메일 클라이언트와 보안
 * 제품이 링크를 미리 열어 보는 일이 흔한데, 여는 것만으로 해지되면 누르지도 않은 사람이
 * 수신거부 처리된다. 메일에서 한 번, 여기서 한 번 — 두 번이지만 로그인이 없으므로
 * 「간편하게」는 지켜진다.
 *
 * 주소는 가려서 보여준다. 로그인 없이 열리는 화면이라 링크만 주우면 남의 메일 주소를
 * 알 수 있게 되면 안 된다.
 */
export function UnsubscribePage(): ReactElement {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const setMarketingOptIn = useSession((state) => state.setMarketingOptIn);
  const unsubscribe = useUnsubscribe();

  // 토큰이 없으면 메일을 거치지 않고 들어온 것이다. 여기서 할 수 있는 일이 없다
  if (!token) {
    return (
      <Shell title="수신거부">
        <div className="flex flex-col gap-4 text-[14px] leading-[1.8] text-muted-foreground">
          <p>
            수신거부는 보내드린 메일 하단의 링크로 들어오셔야 처리할 수 있습니다. 주소창에 직접
            입력하시면 어느 분인지 알 수 없습니다.
          </p>
          <p>
            로그인하실 수 있으면{' '}
            <Link to="/my/account" className="underline underline-offset-4">
              마이페이지 &gt; 계정
            </Link>
            에서 바로 끄실 수 있습니다.
          </p>
          <p>메일이 없으시면 {COMPANY.email} 로 알려주시면 저희가 처리해 드립니다.</p>
        </div>
      </Shell>
    );
  }

  if (unsubscribe.isSuccess) {
    return (
      <Shell title="수신거부 완료">
        <div className="flex flex-col gap-4 text-[14px] leading-[1.8] text-muted-foreground">
          <p className="text-[15px] text-foreground">
            <b className="font-semibold">{unsubscribe.data.maskedEmail}</b> 로는 광고성 정보를 더
            보내지 않습니다.
          </p>
          <p>
            자료 다운로드 링크, 문의 답변처럼 요청하신 내용을 담은 메일은 이 설정과 무관하게
            보내드립니다.
          </p>
          <p>
            다시 받고 싶으시면 로그인 후{' '}
            <Link to="/my/account" className="underline underline-offset-4">
              마이페이지 &gt; 계정
            </Link>
            에서 켜실 수 있습니다.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="수신거부">
      <div className="flex flex-col gap-5">
        <p className="text-[14px] leading-[1.8] text-muted-foreground">
          아래 버튼을 누르시면 광고성 정보 수신을 해지합니다. 로그인하지 않으셔도 됩니다.
        </p>

        <Button
          size="lg"
          disabled={unsubscribe.isPending}
          onClick={() => {
            unsubscribe.mutate(token, {
              // 같은 브라우저에 로그인해 있으면 마이페이지 표시도 함께 맞춘다
              onSuccess: () => setMarketingOptIn(false, TODAY),
            });
          }}
        >
          {unsubscribe.isPending ? '처리 중…' : '광고성 정보 수신 거부하기'}
        </Button>

        {unsubscribe.isError ? (
          <p className="text-[13px] leading-[1.8] text-destructive">
            처리하지 못했습니다. 링크가 만료되었거나 이미 해지된 주소일 수 있습니다. {COMPANY.email}{' '}
            로 알려주시면 저희가 확인해 처리해 드립니다.
          </p>
        ) : null}

        <p className="text-[13px] leading-[1.8] text-muted-foreground">
          자료 다운로드 링크와 문의 답변 메일은 이 설정과 무관하게 계속 보내드립니다.
        </p>
      </div>
    </Shell>
  );
}
